/**
 * Taste Profile Digest Service
 *
 * Uses Gemini to periodically digest raw feedback events into a structured
 * taste profile. The profile refines over time as more feedback accumulates.
 */

import {
  FeedbackEvent,
  TasteProfile,
  setTasteProfile,
  markEventsDigested,
} from '../redux/feedbackSlice';
import { saveTasteProfile, loadAllFeedbackEvents } from './feedbackSyncService';
import type { TasteAxes } from './tasteAxes';
import { trackLLMGeneration } from './analyticsService';
import { getAuthHeaders } from './apiClient';
import { getApiBaseUrl } from '../config/apiBase';

// ---- Configuration ----

const DIGEST_THRESHOLD = 5;

// ---- Public helpers (exported for testing) ----

/**
 * Returns true when enough undigested events have accumulated to warrant a digest.
 */
export const shouldTriggerDigest = (
  totalEvents: number,
  lastDigestedIndex: number,
): boolean => {
  return totalEvents - lastDigestedIndex >= DIGEST_THRESHOLD;
};

/**
 * Build the Gemini prompt for digesting feedback into a taste profile.
 */
export const buildDigestPrompt = (
  currentProfile: TasteProfile | null,
  events: FeedbackEvent[],
  axes?: TasteAxes | null,
): string => {
  const profileSection = currentProfile
    ? `## Current Taste Profile\n\n\`\`\`json\n${JSON.stringify(currentProfile, null, 2)}\n\`\`\``
    : `## Current Taste Profile\n\nThere is no existing profile — this is the first digest. Create a new profile from scratch.`;

  // The three onboarding axes are the prior this digest refines. Without them
  // the model re-derives taste from gestures alone and can end up contradicting
  // what the cook answered outright — and both halves feed the same ranking.
  const axesSection = axes
    ? `## What the cook told us up front\n\n- Plate: ${axes.base === 'protein' ? 'animal protein at the centre' : 'plant-forward'}\n- Weeknight time: ${axes.time === 'quick' ? 'about 30 minutes' : 'as long as it takes'}\n- Seasoning: ${axes.heat === 'bold' ? 'bold and spiced' : 'mild and gentle'}\n\nRefine this reading; contradict it only where the events below clearly disagree.`
    : '';

  const eventsSection = events
    .map(
      (e, i) =>
        `${i + 1}. [${e.type}/${e.action}] ${e.recipeName || '(no recipe)'} — ${e.detail || e.quickReason || '(no detail)'} (${e.date})`,
    )
    .join('\n');

  return `You are a personal food preference analyst. Your job is to refine a user's taste profile based on their meal planning feedback.

## Instructions

- If a profile already exists, refine it — do NOT rewrite from scratch. Preserve existing preferences unless new evidence contradicts them.
- If there is no existing profile, create one from the feedback events.
- Increment the version number by 1.
- Update lastUpdated to the current date.
- Set feedbackCount to the total number of events digested (previous count + new events).
- Keep recentInsights to the 5 most recent learnings.
- Append a new entry to changeLog describing what changed in this version. Keep only the last 10 entries.
- The cook's library is French and English. Whenever a preference names a food,
  a cuisine or a technique, give BOTH words, separated by a slash — "chicken /
  poulet", "sheet-pan / plaque". The scorer matches these words against recipe
  titles and ingredient lines as written, so an English-only term scores nothing
  against a French recipe.
- The preferences object should use these keys where applicable: weeknight_style, weekend_style, proteins, cuisines, dislikes, household, portions, day_affinities. Add keys only when there is evidence. Each value is a short human-readable description.
- Return ONLY valid JSON matching the exact structure below. No markdown, no explanation, just JSON.

## Output Structure

{
  "version": <number>,
  "lastUpdated": "<ISO date>",
  "feedbackCount": <number>,
  "preferences": {
    "<key>": "<description>",
    ...
  },
  "recentInsights": [
    { "date": "<YYYY-MM-DD>", "insight": "<string>" }
  ],
  "changeLog": [
    { "version": <number>, "date": "<YYYY-MM-DD>", "summary": "<what changed>" }
  ]
}

${profileSection}

${axesSection}

## Feedback Events (${events.length} events)

${eventsSection}
`;
};

/**
 * Parse Gemini's response text into a TasteProfile.
 * Handles markdown code blocks and validates required fields.
 * Returns null if parsing fails.
 */
export const parseDigestResponse = (
  responseText: string,
): TasteProfile | null => {
  if (!responseText || !responseText.trim()) return null;

  try {
    // Strip markdown code block if present
    let json = responseText.trim();
    const codeBlockMatch = json.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
    if (codeBlockMatch) {
      json = codeBlockMatch[1].trim();
    }

    const parsed = JSON.parse(json);

    // Validate required fields
    if (
      typeof parsed.version !== 'number' ||
      typeof parsed.lastUpdated !== 'string' ||
      typeof parsed.feedbackCount !== 'number' ||
      typeof parsed.preferences !== 'object' ||
      !Array.isArray(parsed.recentInsights) ||
      !Array.isArray(parsed.changeLog)
    ) {
      return null;
    }

    return parsed as TasteProfile;
  } catch {
    return null;
  }
};

// ---- Main digest function ----

/**
 * Run a taste profile digest: loads all events from Firestore, calls Gemini,
 * and dispatches the updated profile to Redux (which triggers Firestore sync
 * via the middleware).
 *
 * @param dispatch - Redux dispatch function
 * @param getState - Redux getState function
 * @param userId - Current user's ID
 * @returns The updated profile, or null on failure
 */
export const digestFeedback = async (
  dispatch: (action: any) => void,
  getState: () => any,
  userId: string,
): Promise<TasteProfile | null> => {
  try {
    // 1. Load all events from Firestore (full history for context)
    const allEvents = await loadAllFeedbackEvents(userId);
    if (allEvents.length === 0) return null;

    // 2. Get current profile from Redux
    const state = getState();
    const currentProfile: TasteProfile | null =
      state.feedback?.tasteProfile ?? null;

    // 3. Build prompt and call server-side route
    const prompt = buildDigestPrompt(currentProfile, allEvents, state.userPreferences?.tasteAxes ?? null);
    const headers = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/ai/taste-profile`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.warn('tasteProfileService: Server error:', err.error);
      return null;
    }

    const data = await response.json();

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'taste_profile_digest',
      $ai_model: data.model,
      $ai_provider: 'google',
      $ai_input_tokens: data.input_tokens,
      $ai_output_tokens: data.output_tokens,
      $ai_latency: data.latency_s,
    });

    // 4. Parse response
    const updatedProfile = parseDigestResponse(data.result);
    if (!updatedProfile) {
      console.warn('tasteProfileService: Failed to parse Gemini response');
      return null;
    }

    // 5. Save to Redux (middleware auto-syncs to Firestore)
    dispatch(setTasteProfile(updatedProfile));
    dispatch(markEventsDigested(state.feedback.events.length));

    return updatedProfile;
  } catch (error) {
    console.warn('tasteProfileService: Digest failed:', error);
    return null;
  }
};
